import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ShoppingCart, ArrowRight, Store, Loader2, X, Filter, Search } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { getImageUrl } from "../utils/imageUtils";
import { cn } from "@/lib/utils";

// Use public axios instance
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicAxios = axios.create({ withCredentials: false });

export default function ShopHome() {
    const navigate = useNavigate();
    const location = useLocation();

    // State
    const [products, setProducts] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [activeCollection, setActiveCollection] = useState(null); // ID of selected collection
    const [activeCollectionName, setActiveCollectionName] = useState("");
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar toggle
    const [collectionSearch, setCollectionSearch] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    // Sorted and filtered collections
    const filteredCollections = useMemo(() => {
        return collections
            .filter(c => c.name.toLowerCase().includes(collectionSearch.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, [collections, collectionSearch]);

    // Refs for infinite scroll
    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Initial Load: Fetch collections
    useEffect(() => {
        fetchCollections();
    }, []);

    // Handle URL params and Fetching
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const q = searchParams.get("search") || "";
        const colId = searchParams.get("collection_id") || null;

        // Sync local state for UI components
        setSearchTerm(q);
        setActiveCollection(colId);

        // Reset and Fetch
        setProducts([]);
        setPage(1);
        setHasMore(true);
        fetchProducts(1, colId, q);
    }, [location.search]);

    // Fetch more pages
    useEffect(() => {
        if (page > 1) {
            fetchProducts(page, activeCollection, searchTerm);
        }
    }, [page]);

    // Update activeCollectionName when collections are loaded or activeCollection changes
    useEffect(() => {
        if (activeCollection && collections.length > 0) {
            const col = collections.find(c => c._id === activeCollection || c.collection_id === activeCollection);
            if (col) {
                setActiveCollectionName(col.name);
            }
        } else if (!activeCollection) {
            setActiveCollectionName("");
        }
    }, [activeCollection, collections]);

    const fetchCollections = async () => {
        try {
            const response = await publicAxios.get(`${API}/collections?active_only=true`);
            setCollections(response.data);
        } catch (error) {
            console.error("Failed to fetch collections:", error);
        }
    };

    const fetchProducts = async (pageNum, collectionId, keyword = "") => {
        setLoading(true);
        try {
            const limit = 12;
            let url = `${API}/products?limit=${limit}&page=${pageNum}&only_parents=true&is_active=true`;

            if (collectionId) {
                url += `&collection_id=${collectionId}`;
            }
            if (keyword) {
                url += `&keyword=${encodeURIComponent(keyword)}`;
            }

            const response = await publicAxios.get(url);
            const newProducts = response.data.products || response.data || [];

            setProducts(prev => pageNum === 1 ? newProducts : [...prev, ...newProducts]);
            setHasMore(newProducts.length === limit);
        } catch (error) {
            console.error("Failed to fetch products:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCollectionClick = (collection) => {
        const id = collection._id;
        const searchParams = new URLSearchParams(location.search);

        if (activeCollection === id) {
            searchParams.delete("collection_id");
        } else {
            searchParams.set("collection_id", id);
        }

        const newSearch = searchParams.toString();
        navigate(newSearch ? `/products?${newSearch}` : '/products');
        setIsSidebarOpen(false); // Close mobile sidebar on selection
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearFilter = () => {
        setActiveCollection(null);
        setActiveCollectionName("");
        setSearchTerm("");
        navigate('/products');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans animate-fade-in">
            <Header />

            {/* Breadcrumb & Top Bar */}
            <div className="bg-white border-b py-4 px-4 lg:px-8 mt-16 md:mt-20">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
                        <Link to="/" className="hover:text-amber-700">Home</Link>
                        <ArrowRight size={10} />
                        <span className="text-slate-900 font-bold">Shop</span>
                        {activeCollectionName && (
                            <>
                                <ArrowRight size={10} />
                                <span className="text-amber-700 font-bold">{activeCollectionName}</span>
                            </>
                        )}
                    </div>

                    {/* Mobile Filter Toggle */}
                    <Button
                        variant="outline"
                        size="sm"
                        className="md:hidden flex items-center gap-2"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    >
                        <Filter size={16} />
                        {isSidebarOpen ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row gap-8 lg:gap-12">

                    {/* LEFT SIDEBAR: Collections */}
                    <aside className={cn(
                        "md:w-64 flex-shrink-0 transition-all duration-300",
                        isSidebarOpen ? "block" : "hidden md:block"
                    )}>
                        <div className="sticky top-28">
                            <div className="bg-amber-900 text-white p-4 rounded-t-lg shadow-sm">
                                <h3 className="font-bold text-lg tracking-wide">Collections</h3>
                            </div>
                            <div className="bg-white shadow-sm border border-t-0 border-slate-100">
                                <div className="p-2 border-b border-slate-100">
                                    <div className="relative">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search collections..."
                                            value={collectionSearch}
                                            onChange={(e) => setCollectionSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white rounded-b-lg shadow-sm border border-t-0 border-slate-100 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
                                {filteredCollections.length === 0 && (
                                    <div className="p-4 text-sm text-slate-400 text-center">No collections found</div>
                                )}
                                {filteredCollections.map((collection) => (
                                    <button
                                        key={collection._id}
                                        onClick={() => handleCollectionClick(collection)}
                                        className={cn(
                                            "w-full text-left px-4 py-3 text-sm font-medium transition-colors border-l-4 hover:bg-slate-50",
                                            activeCollection === collection._id
                                                ? "border-amber-600 bg-amber-50 text-amber-900"
                                                : "border-transparent text-slate-600 hover:text-amber-700"
                                        )}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span>{collection.name}</span>
                                            {activeCollection === collection._id && <ArrowRight size={14} />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </aside>

                    {/* RIGHT PANEL: Product Grid */}
                    <div className="flex-1 min-w-0">

                        {/* Active Filter Tags */}
                        <div className="flex flex-wrap gap-2 mb-6 animate-in fade-in slide-in-from-top-2">
                            {activeCollectionName && (
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-sm font-bold shadow-sm border border-amber-200">
                                    Collection: {activeCollectionName}
                                    <button onClick={() => { setActiveCollection(null); setActiveCollectionName(""); navigate(`/products${searchTerm ? '?search=' + encodeURIComponent(searchTerm) : ''}`); }} className="hover:text-red-600 p-0.5 rounded-full hover:bg-amber-200/50 transition-colors">
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                            {searchTerm && (
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-bold shadow-sm border border-slate-200">
                                    Search: "{searchTerm}"
                                    <button onClick={() => { setSearchTerm(""); navigate(`/products${activeCollection ? '?collection_id=' + activeCollection : ''}`); }} className="hover:text-red-600 p-0.5 rounded-full hover:bg-slate-200 transition-colors">
                                        <X size={14} />
                                    </button>
                                </span>
                            )}
                        </div>

                        {/* Loading State (Initial) */}
                        {loading && page === 1 && products.length === 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl p-4 h-80 animate-pulse border border-slate-100">
                                        <div className="h-48 bg-slate-100 rounded-xl mb-4"></div>
                                        <div className="h-4 bg-slate-100 rounded w-3/4 mb-2"></div>
                                        <div className="h-4 bg-slate-100 rounded w-1/2"></div>
                                    </div>
                                ))}
                            </div>
                        ) : products.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {products.map((product, index) => {
                                    // Attach ref to the last element for infinite scroll
                                    const isLast = index === products.length - 1;
                                    return (
                                        <div
                                            key={`${product.product_id}-${index}`}
                                            ref={isLast ? lastProductElementRef : null}
                                            className="bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 group border border-slate-50 flex flex-col"
                                        >
                                            <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-slate-100 mb-4">
                                                <Link to={`/product/${product.product_id}`}>
                                                    {product.image_urls?.[0] || product.image_url ? (
                                                        <img
                                                            src={getImageUrl(product.image_urls?.[0] || product.image_url)}
                                                            alt={product.name}
                                                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400">
                                                            <Store size={32} />
                                                        </div>
                                                    )}
                                                </Link>
                                                {/* Stock / Availability Badge */}
                                                {(product.is_active === false) && (
                                                    <div className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur text-xs font-bold rounded-md shadow-sm text-red-600">
                                                        Not Available
                                                    </div>
                                                )}
                                                {/* Hover Actions */}
                                                {product.is_active !== false && (
                                                    <button className="absolute bottom-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full text-slate-800 hover:bg-slate-900 hover:text-white opacity-0 group-hover:opacity-100 transition-all shadow-sm translate-y-2 group-hover:translate-y-0">
                                                        <ShoppingCart size={16} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-2 flex-grow flex flex-col">
                                                <Link to={`/product/${product.product_id}`} className="block flex-grow">
                                                    <h3 className="font-semibold text-slate-900 line-clamp-2 hover:text-amber-700 transition-colors" style={{ fontSize: '1.125rem' }}>
                                                        {product.name}
                                                    </h3>
                                                </Link>
                                                <div className="pt-2">
                                                    <span className="font-bold text-amber-900 text-lg">
                                                        ${(product.display_price || product.base_price || product.price || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-white rounded-3xl border border-slate-100">
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                                    <Store size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">No products found</h3>
                                <p className="text-slate-500">Try selecting a different collection or clearing filters.</p>
                                <Button variant="outline" onClick={clearFilter} className="mt-4">Clear Filters</Button>
                            </div>
                        )}

                        {/* Infinite Scroll Loader */}
                        {loading && page > 1 && (
                            <div className="py-8 flex justify-center">
                                <div className="flex items-center gap-2 text-slate-400 text-sm font-medium animate-pulse">
                                    <Loader2 size={20} className="animate-spin" />
                                    Loading more products...
                                </div>
                            </div>
                        )}

                        {/* End of List Message */}
                        {!hasMore && products.length > 0 && (
                            <div className="py-12 text-center text-slate-400 text-sm font-medium uppercase tracking-widest border-t border-slate-100 mt-12 mx-auto max-w-xs">
                                No more products
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
